/**
 * Impersonation Store
 *
 * Lets platform admins and staff "become" any user to see exactly
 * what they see. Saves the admin session and restores on exit.
 * Only accessible to platform_admin and staff roles.
 */

import { createWithEqualityFn } from 'zustand/traditional'
import { shallow } from 'zustand/shallow'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { NavigateFunction } from 'react-router-dom'
import { useAuthStore } from './useAuthStore'
import { adminApi } from '../api'
import { buildContextsFromLegacyRole, getDefaultActiveContext } from '../lib/contextHelpers'

interface ImpersonationState {
  isImpersonating: boolean
  targetUserId: string | null
  targetUserName: string | null
  targetUserEmail: string | null
  targetUserRole: string | null
  savedAdminSession: string | null
  savedAdminAuthStore: string | null
}

interface ImpersonationActions {
  startImpersonation: (userId: string, navigate: NavigateFunction) => Promise<void>
  endImpersonation: (navigate: NavigateFunction) => void
}

type ImpersonationStore = ImpersonationState & ImpersonationActions

const ALLOWED_ROLES = ['platform_admin', 'staff']

export const useImpersonationStore = createWithEqualityFn<ImpersonationStore>()(
  persist(
    (set, get) => ({
      isImpersonating: false,
      targetUserId: null,
      targetUserName: null,
      targetUserEmail: null,
      targetUserRole: null,
      savedAdminSession: null,
      savedAdminAuthStore: null,

      startImpersonation: async (userId: string, navigate: NavigateFunction) => {
        // Guard: only admins/staff can impersonate
        const currentUser = useAuthStore.getState().user
        if (!currentUser || !ALLOWED_ROLES.includes(currentUser.role)) return

        // Don't impersonate yourself
        if (currentUser.id === userId) return

        // Snapshot admin session
        const adminSession = localStorage.getItem('docurious-session')
        const adminAuth = localStorage.getItem('docurious-auth')

        // Call admin API to switch session
        const response = await adminApi.impersonateUser(userId)
        if (!response.success || !response.data) return

        const { user, tokens } = response.data

        // Enrich impersonated user with contexts
        const enrichedUser = user.contexts ? user : { ...user, contexts: buildContextsFromLegacyRole(user) }
        const activeContext = getDefaultActiveContext(enrichedUser)

        // Update auth store with impersonated user
        useAuthStore.setState({
          user: enrichedUser,
          tokens,
          isAuthenticated: true,
          activeContext,
          activeWorkspace: activeContext === 'school' ? 'school' : 'personal',
        })

        set({
          isImpersonating: true,
          targetUserId: user.id,
          targetUserName: user.displayName,
          targetUserEmail: user.email,
          targetUserRole: user.role,
          savedAdminSession: adminSession,
          savedAdminAuthStore: adminAuth,
        })

        navigate('/', { replace: true })
      },

      endImpersonation: (navigate: NavigateFunction) => {
        const { savedAdminSession, savedAdminAuthStore } = get()

        // Restore admin session
        if (savedAdminSession) {
          localStorage.setItem('docurious-session', savedAdminSession)
        } else {
          localStorage.removeItem('docurious-session')
        }

        if (savedAdminAuthStore) {
          localStorage.setItem('docurious-auth', savedAdminAuthStore)
        } else {
          localStorage.removeItem('docurious-auth')
        }

        set({
          isImpersonating: false,
          targetUserId: null,
          targetUserName: null,
          targetUserEmail: null,
          targetUserRole: null,
          savedAdminSession: null,
          savedAdminAuthStore: null,
        })

        // Rehydrate auth store from restored admin session
        useAuthStore.getState().initialize()

        navigate('/admin/users', { replace: true })
      },
    }),
    {
      name: 'docurious-impersonation',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        isImpersonating: state.isImpersonating,
        targetUserId: state.targetUserId,
        targetUserName: state.targetUserName,
        targetUserEmail: state.targetUserEmail,
        targetUserRole: state.targetUserRole,
        savedAdminSession: state.savedAdminSession,
        savedAdminAuthStore: state.savedAdminAuthStore,
      }),
    },
  ),
  shallow,
)
